// src/pages/ModulesCenter.tsx - Kompaktowa wersja z dark theme
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODULES } from '../config/modules.config';
import * as Icons from 'lucide-react';

export default function ModulesCenter() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Pobierz unikalne kategorie
  const categories = useMemo(() => {
    const cats = [...new Set(MODULES.map(m => m.category))];
    return cats.sort();
  }, []);

  // Filtruj moduły
  const filteredModules = useMemo(() => {
    let filtered = MODULES;
    
    if (searchTerm) {
      filtered = filtered.filter(m => 
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(m => m.category === selectedCategory);
    }
    
    return filtered;
  }, [searchTerm, selectedCategory]);

  // Grupuj moduły po kategoriach
  const groupedModules = useMemo(() => {
    const groups: Record<string, typeof MODULES> = {};
    filteredModules.forEach(module => {
      if (!groups[module.category]) {
        groups[module.category] = [];
      }
      groups[module.category].push(module);
    });
    return groups;
  }, [filteredModules]);

  const getIcon = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons] as React.ComponentType<{ className?: string }>;
    return Icon ? <Icon className="h-5 w-5" /> : <Icons.Box className="h-5 w-5" />;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'Sprzedaż': 'ShoppingBag',
      'Produkcja': 'Factory',
      'Logistyka': 'Truck',
      'Materiały': 'Package',
      'Finanse': 'DollarSign',
      'AI': 'Sparkles',
      'Administracja': 'Shield',
      'Wiedza': 'BookOpen',
      'Posprzedaż': 'RotateCcw'
    };
    return icons[category] || 'Folder';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Sprzedaż': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
      'Produkcja': 'text-green-400 bg-green-500/10 border-green-500/20',
      'Logistyka': 'text-orange-400 bg-orange-500/10 border-orange-500/20',
      'Materiały': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
      'Finanse': 'text-red-400 bg-red-500/10 border-red-500/20',
      'AI': 'text-pink-400 bg-pink-500/10 border-pink-500/20',
      'Administracja': 'text-gray-400 bg-gray-500/10 border-gray-500/20',
      'Wiedza': 'text-teal-400 bg-teal-500/10 border-teal-500/20',
      'Posprzedaż': 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
    };
    return colors[category] || 'text-gray-400 bg-gray-500/10 border-gray-500/20';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-slate-900 text-white">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Kompaktowy header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Icons.Grid3X3 className="h-6 w-6 text-purple-400" />
              Centrum Modułów
            </h1>
            <p className="text-sm text-gray-400 mt-1">Wszystkie moduły w jednym miejscu</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Szukaj..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 w-48"
            />
          </div>
        </div>

        {/* Kompaktowe filtry kategorii */}
        <div className="mb-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              !selectedCategory 
                ? 'bg-purple-600 text-white' 
                : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 border border-zinc-700'
            }`}
          >
            Wszystkie ({MODULES.length})
          </button>
          {categories.map(cat => {
            const count = MODULES.filter(m => m.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 text-xs rounded-lg transition-all flex items-center gap-1 ${
                  selectedCategory === cat 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 border border-zinc-700'
                }`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Kompaktowa siatka modułów */}
        <div className="space-y-4">
          {Object.entries(groupedModules).map(([category, modules]) => {
            const CategoryIcon = Icons[getCategoryIcon(category) as keyof typeof Icons] || Icons.Folder;
            
            return (
              <div key={category} className="bg-zinc-800/50 backdrop-blur rounded-xl border border-zinc-700/50 p-4">
                {/* Nagłówek kategorii */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-1.5 rounded-lg ${getCategoryColor(category)}`}>
                    <CategoryIcon className="h-4 w-4" />
                  </div>
                  <h2 className="text-sm font-semibold text-white">{category}</h2>
                  <span className="text-xs text-gray-500">({modules.length})</span>
                </div>
                
                {/* Kompaktowa siatka modułów */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {modules.map(module => {
                    const ModuleIcon = Icons[module.icon as keyof typeof Icons] || Icons.Box;
                    
                    return (
                      <button
                        key={module.id}
                        onClick={() => navigate(module.path)}
                        className={`
                          relative p-3 rounded-lg transition-all duration-200
                          bg-zinc-900/50 border border-zinc-700/50
                          hover:bg-zinc-800 hover:border-zinc-600 hover:scale-[1.02]
                          ${module.featured ? 'ring-1 ring-purple-500/50' : ''}
                        `}
                      >
                        {module.featured && (
                          <div className="absolute -top-1 -right-1">
                            <span className="flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                            </span>
                          </div>
                        )}
                        
                        <div className="flex flex-col items-center gap-2">
                          <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                            <ModuleIcon className="h-5 w-5" />
                          </div>
                          <span className="text-xs text-gray-200 text-center leading-tight">
                            {module.name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredModules.length === 0 && (
          <div className="text-center py-12">
            <Icons.Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Nie znaleziono modułów</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory(null);
              }}
              className="mt-4 text-purple-400 hover:text-purple-300"
            >
              Wyczyść filtry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
